import { forwardRef, useCallback, useMemo } from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import type { ProcessDataFromClipboardParams } from 'ag-grid-community';
import { AG_GRID_LOCALE_DE } from '@ag-grid-community/locale';
import { useTranslation } from 'react-i18next';
import getLoadingOverlay from './LoadingOverlay';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './agGridStyle.scss';
import NoRowsOverlay from './NoRowsOverlay';

const DISTANCE_TO_TABLE = 250; //px

const headerComponentParams = {
    template: `<div class="ag-cell-label-container" role="presentation">
    <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button" aria-hidden="true"></span>
    <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button" aria-hidden="true"></span>
    <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
        <span data-ref="eText" class="ag-header-cell-text" style="white-space: pre-line;"></span>
        <span data-ref="eFilter" class="ag-header-icon ag-header-label-icon ag-filter-icon" aria-hidden="true"></span>
        <span data-ref="eSortOrder" class="ag-header-icon ag-header-label-icon ag-sort-order" aria-hidden="true"></span>
        <span data-ref="eSortAsc" class="ag-header-icon ag-header-label-icon ag-sort-ascending-icon" aria-hidden="true"></span>
        <span data-ref="eSortDesc" class="ag-header-icon ag-header-label-icon ag-sort-descending-icon" aria-hidden="true"></span>
        <span data-ref="eSortNone" class="ag-header-icon ag-header-label-icon ag-sort-none-icon" aria-hidden="true"></span>
    </div>
</div>`,
};

interface TableProps extends AgGridReactProps {
    height?: string;
    defaultColDef?: any;
}

/**
 * Processes clipboard data before paste to remove trailing empty rows.
 *
 * Excel includes a trailing newline after the last row when copying data,
 * which AG Grid interprets as an additional empty row. Combined with
 * valueSetter implementations that convert empty strings to 0, this causes
 * an extra "0" to be inserted in the first cell below the paste area.
 *
 * This function strips trailing rows where ALL cells are empty, preventing
 * the issue while preserving intentionally empty cells within the data.
 */
const processDataFromClipboard = (params: ProcessDataFromClipboardParams): string[][] | null => {
    const data = params.data;

    // If no data, return as-is
    if (!data || data.length === 0) {
        return data;
    }

    // Remove trailing rows where ALL cells are empty strings
    let lastNonEmptyRowIndex = data.length - 1;

    while (lastNonEmptyRowIndex >= 0) {
        const row = data[lastNonEmptyRowIndex];

        // Treat undefined/null rows as empty (skip them)
        if (!row) {
            lastNonEmptyRowIndex--;
            continue;
        }

        const isRowEmpty = row.every((cell) => cell === '' || cell === null || cell === undefined);

        if (!isRowEmpty) {
            break;
        }
        lastNonEmptyRowIndex--;
    }

    // Return data without trailing empty rows
    return data.slice(0, lastNonEmptyRowIndex + 1);
};

const Table = forwardRef<any, TableProps>(({ children, height, defaultColDef, ...props }, ref) => {
    const { t, i18n } = useTranslation();
    const language = i18n.resolvedLanguage ?? 'de';

    // AG Grid's built-in strings are English, so only German needs an override map.
    const localeText = useMemo(() => (language === 'de' ? AG_GRID_LOCALE_DE : undefined), [language]);

    const loadingOverlayTemplate = useMemo(() => getLoadingOverlay(t('common.loading')), [t]);
    const noRowsOverlayComponent = useCallback(() => <NoRowsOverlay text={t('common.noData')} />, [t]);

    // Memoize with deep comparison to prevent AG Grid from calling recreateColumnDefs()
    // on every render. AG Grid uses shallow !== to detect prop changes, and when
    // defaultColDef changes reference, it triggers a full column rebuild that
    // resets user column state (hide, sort, width, etc.).
    const defaultColDefKey = JSON.stringify(defaultColDef);
    const mergedDefaultColDef = useMemo(
        () => ({
            editable: true,
            sortable: true,
            resizable: true,
            filter: 'agMultiColumnFilter' as const,
            minWidth: 70,
            flex: 1,
            headerComponentParams: headerComponentParams,
            cellClass: 'notranslate',
            ...defaultColDef,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [defaultColDefKey]
    );

    return (
        <div
            className="ag-theme-material"
            style={{
                // autoHeight grids size themselves to their rows — the fixed floor would pad
                // short grids (e.g. the one-row scenario mini-grids) with a dead empty strip.
                minHeight: props.domLayout === 'autoHeight' ? undefined : 100,
                height: height ? height : `calc(100vh - ${DISTANCE_TO_TABLE}px)`,
                width: '100%',
            }}
        >
            <AgGridReact
                // localeText is an initial grid option: AG Grid reads it at creation
                // and not on prop updates, so pagination/filter/tool-panel chrome
                // would stay in the previous language after a switch. AG Grid's own
                // language-switch guidance is to destroy and recreate the grid;
                // keying on the language is the React equivalent. Grid state
                // (widths, filters, expansion) is lost on switch — acceptable for an
                // explicit, rare user action.
                key={language}
                ref={ref}
                localeText={localeText}
                defaultColDef={mergedDefaultColDef}
                animateRows={true}
                undoRedoCellEditing={true}
                undoRedoCellEditingLimit={10}
                overlayLoadingTemplate={loadingOverlayTemplate}
                noRowsOverlayComponent={noRowsOverlayComponent}
                suppressCsvExport={true}
                suppressExcelExport={true}
                rowHeight={32}
                popupParent={document.body}
                processDataFromClipboard={processDataFromClipboard}
                {...props}
            >
                {children}
            </AgGridReact>
        </div>
    );
});

export default Table;
